<template>
    <div class="widget-stat-chart-wrapper">
        <WidgetStatChart
            class="widget-stat-chart"
            :title="$t('averageAvailability')"
            :value="$format.percent(stats.avgAvailability)"
            :loading="request.getLoading()"
            tooltip-formatter="round"
            :title-tooltip="$t('averageAvailabilityTooltipText')"
        />
        <WidgetStatChart
            class="widget-stat-chart"
            :title="$t('averageAvailabilityQuantitatively')"
            :value="stats.avgAvailabilityQuantitatively"
            :loading="request.getLoading()"
            tooltip-formatter="round"
            :title-tooltip="$t('averageAvailabilityQuantitativelyTooltipText')"
        />
        <WidgetStatChart
            class="widget-stat-chart"
            :title="$t('numberOfProducts')"
            :value="stats.products"
            :loading="request.getLoading()"
            tooltip-formatter="round"
            :title-tooltip="$t('numberOfProductsTooltipText')"
        />
    </div>
</template>



<script>
    import { calculateChangePercentage } from '@/utils/number';
    import WidgetStatChart from '@/views/common/dashboard/parts/WidgetStatChart.vue';

    export default {
        name: 'StatsWidgets',
        components: {
            WidgetStatChart,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.parentComponent.requestData,
            };
        },
        computed: {
            timeRange() {
                return this.parentComponent.timeRange;
            },
            campaigns() {
                return this.parentComponent.campaigns;
            },
            data() {
                return this.parentComponent.data;
            },
            stats() {
                return this.calculateStats(this.data, this.timeRange.startDate, this.timeRange.endDate, this.campaigns);
            },
        },
        methods: {
            percentDiff(newValue, oldValue) {
                return calculateChangePercentage(newValue, oldValue);
            },
            calculateStats(products, startDate, endDate, campaigns) {
                const startTime = new Date(startDate).getTime();
                const endTime = new Date(endDate).getTime();
                const totalPeriod = endTime - startTime;

                if (!products || totalPeriod <= 0) {
                    return { avgAvailability: 0, avgAvailabilityQuantitatively: 0, products: 0 };
                }

                const productAvailabilities = Object.values(products).map(product => {
                    const charts = product.charts || {};

                    const availabilityTimes = campaigns.map(campaignId => {
                        const key = String(campaignId);
                        if (!charts[key]) return 0;

                        const segments = charts[key].flatMap(offer => offer.chart?.segments ?? []);
                        const mergedSegments = this.mergeSegments(segments);
                        const limitedSegments = this.limitSegments(mergedSegments, startTime, endTime);

                        return limitedSegments.reduce((sum, seg) => sum + (seg.end - seg.start), 0);
                    });

                    const avgTime = availabilityTimes.reduce((sum, time) => sum + time, 0) / availabilityTimes.length;
                    return (avgTime / totalPeriod) * 100;
                });

                const overallAvg = productAvailabilities.reduce((sum, avail) => sum + avail, 0) / productAvailabilities.length || 0;
                const productsCount = Object.keys(products).length;

                return {
                    avgAvailability: overallAvg,
                    avgAvailabilityQuantitatively: Math.round((productsCount * overallAvg) / 100),
                    products: productsCount,
                };
            },
            mergeSegments(segments) {
                if (!segments?.length) return [];
                const sorted = segments.slice().sort((a, b) => a.start - b.start);
                return sorted.reduce((merged, current) => {
                    const last = merged[merged.length - 1];
                    if (last && last.end >= current.start) {
                        last.end = Math.max(last.end, current.end);
                    } else {
                        merged.push({ ...current });
                    }
                    return merged;
                }, []);
            },
            limitSegments(segments, limitStart, limitEnd) {
                return segments
                    .map(seg => ({
                        start: Math.max(seg.start, limitStart),
                        end: Math.min(seg.end, limitEnd),
                    }))
                    .filter(seg => seg.start < seg.end);
            },
        },
    };
</script>
